/**
 * A host-realm module's code, and whether it has arrived (RFC 0284 §4d).
 *
 * Every host-realm mount does the same three things before it can draw: open
 * the package's view document, read the module out of it, and decide what to
 * show while that is in flight. `InstalledSlot`, `Thread` and
 * `DocumentView` each had their own copy, and the copies had drifted: two
 * carried the same loading message twice over, and the third fell through to
 * the built-in editor while the installed one's code was still arriving, so a
 * person saw one editor and then, a moment later, another.
 *
 * ONE HOOK, so the answer to "is it here yet" is computed once. The mount that
 * calls it decides how to wrap the result; nothing here knows what a slot or a
 * pane is.
 *
 * THREE STATES, not a boolean. `absent` means there is nothing to load, which
 * is the normal case for a leaf nobody installed anything for, and it must not
 * be drawn as "waiting". `loading` means a view document is named and has not
 * produced code yet, which a person should be told about, because a document
 * that never syncs and a document that is slow look identical from a blank
 * surface. `ready` means there is a module to mount.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { readDocumentContent } from '@mod/core/model'
import { useDocument } from '../../services/world/repo/useDocument'

export type ContributionState = 'absent' | 'loading' | 'ready'

export interface Contribution {
  code: string
  state: ContributionState
}

/**
 * The state, as a pure function, so it can be tested without a repo.
 *
 * Empty code with a named document is still LOADING: a view document exists
 * from the moment `installChrome` mints it, and an empty one is either mid-sync
 * or a push that has not landed. Neither is a module worth mounting.
 */
export function contributionState(
  viewDocId: string | undefined,
  code: string,
): ContributionState {
  if (!viewDocId) return 'absent'
  return code ? 'ready' : 'loading'
}

export function useContribution(viewDocId: string | undefined): Contribution {
  // `useDocument` accepts undefined and opens nothing, which is what lets this
  // hook be called unconditionally by a mount that may have nothing to mount.
  const [viewDoc] = useDocument(viewDocId as AnyDocumentId | undefined)
  const code = viewDoc ? readDocumentContent(viewDoc) : ''
  return { code, state: contributionState(viewDocId, code) }
}
